import type { SeatWithUser, UserWithCognitoStatus } from "@api/app-api";
import AddIcon from "@mui/icons-material/Add";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import DeleteIcon from "@mui/icons-material/Delete";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import RefreshIcon from "@mui/icons-material/Refresh";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { apiClient } from "../../api";

function formatUserName(
  user?: SeatWithUser["assignedUser"] | UserWithCognitoStatus,
) {
  if (!user) return "";
  return (
    [user.firstName, user.lastName].filter(Boolean).join(" ") || user.email
  );
}

function formatDate(value: string | null) {
  if (!value) return "—";
  return new Date(value).toLocaleDateString();
}

function isFlexibleSeat(seat: SeatWithUser) {
  return !!seat.isFlexible;
}

function formatSeatType(seat: SeatWithUser) {
  return isFlexibleSeat(seat) ? "Flexible" : "Named";
}

function formatSeatLabel(seat: SeatWithUser) {
  return `${seat.id.slice(0, 8)}... (${formatSeatType(seat)})`;
}

interface SeatAssignment {
  seatId: string;
  userId: string;
}

interface CompanySeatsPanelProps {
  companyId: string;
  users: UserWithCognitoStatus[];
  onSeatsChanged: (seats: SeatWithUser[]) => void;
  addDialogOpen?: boolean;
  onAddDialogOpenChange?: (open: boolean) => void;
  hideHeaderActions?: boolean;
}

export const CompanySeatsPanel: React.FC<CompanySeatsPanelProps> = ({
  companyId,
  users,
  onSeatsChanged,
  addDialogOpen,
  onAddDialogOpenChange,
  hideHeaderActions = false,
}) => {
  const [seats, setSeats] = useState<SeatWithUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [assigningSeat, setAssigningSeat] = useState<SeatWithUser | null>(null);
  const [selectedUserId, setSelectedUserId] = useState("");
  const [savingSeatId, setSavingSeatId] = useState<string | null>(null);
  const [internalAddDialogOpen, setInternalAddDialogOpen] = useState(false);
  const [newSeatCount, setNewSeatCount] = useState("1");
  const [newSeatsFlexible, setNewSeatsFlexible] = useState(true);
  const [autoAssignDialogOpen, setAutoAssignDialogOpen] = useState(false);
  const [autoAssignSelections, setAutoAssignSelections] = useState<
    Record<string, string>
  >({});

  const isAddDialogOpen = addDialogOpen ?? internalAddDialogOpen;
  const setAddDialogOpen = onAddDialogOpenChange ?? setInternalAddDialogOpen;

  const assignedUserIds = useMemo(
    () => new Set(seats.map((seat) => seat.assignedUserId).filter(Boolean)),
    [seats],
  );

  const assignableUsers = useMemo(
    () =>
      users.filter(
        (user) =>
          user.status === "ACTIVE" &&
          (!assignedUserIds.has(user.id) ||
            user.id === assigningSeat?.assignedUserId),
      ),
    [assignedUserIds, assigningSeat?.assignedUserId, users],
  );

  const activeUnseatedUsers = useMemo(
    () =>
      users.filter(
        (user) => user.status === "ACTIVE" && !assignedUserIds.has(user.id),
      ),
    [assignedUserIds, users],
  );

  const availableSeats = useMemo(
    () => seats.filter((seat) => !seat.assignedUserId),
    [seats],
  );

  const selectedAutoAssignUserIds = useMemo(
    () => new Set(Object.values(autoAssignSelections).filter(Boolean)),
    [autoAssignSelections],
  );

  const applySeats = (nextSeats: SeatWithUser[]) => {
    setSeats(nextSeats);
    onSeatsChanged(nextSeats);
  };

  const loadSeats = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const fetchedSeats = await apiClient.getCompanySeats(companyId);
      applySeats(fetchedSeats);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load seats");
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    loadSeats();
  }, [loadSeats]);

  const handleAddSeats = async () => {
    const count = Number.parseInt(newSeatCount, 10);
    if (!Number.isFinite(count) || count < 1) return;

    const previousSeats = seats;
    const now = new Date().toISOString();
    const optimisticSeats: SeatWithUser[] = [
      ...seats,
      ...Array.from({ length: count }, (_, index) => ({
        companyId,
        id: `new-${Date.now()}-${index}`,
        assignedUserId: null,
        lastUse: null,
        lastReassign: now,
        autoReassign: newSeatsFlexible,
        isFlexible: newSeatsFlexible,
      })),
    ];

    setSavingSeatId("new");
    setError(null);
    try {
      applySeats(optimisticSeats);
      const response = await apiClient.addCompanySeats(
        companyId,
        count,
        newSeatsFlexible,
      );
      applySeats(response.seats);
      setAddDialogOpen(false);
      setNewSeatCount("1");
      setNewSeatsFlexible(true);
    } catch (err) {
      applySeats(previousSeats);
      setError(err instanceof Error ? err.message : "Failed to add seats");
    } finally {
      setSavingSeatId(null);
    }
  };

  const handleAssignSeat = async (seatId: string, userId: string | null) => {
    const previousSeats = seats;
    const assignedUser = userId
      ? users.find((user) => user.id === userId)
      : undefined;
    const now = new Date().toISOString();
    const optimisticSeats = seats.map((seat) =>
      seat.id === seatId
        ? {
            ...seat,
            assignedUserId: userId,
            assignedUser,
            lastUse: userId ? now : null,
            lastReassign: now,
          }
        : seat,
    );

    setSavingSeatId(seatId);
    setError(null);
    try {
      applySeats(optimisticSeats);
      const response = await apiClient.assignCompanySeat(
        companyId,
        seatId,
        userId,
      );
      applySeats(
        optimisticSeats.map((seat) =>
          seat.id === seatId ? response.seat : seat,
        ),
      );
      setAssigningSeat(null);
    } catch (err) {
      applySeats(previousSeats);
      setError(err instanceof Error ? err.message : "Failed to update seat");
    } finally {
      setSavingSeatId(null);
    }
  };

  const applySeatAssignments = async (assignments: SeatAssignment[]) => {
    if (assignments.length === 0) {
      setError("Select at least one user to assign a seat.");
      return;
    }

    const seenSeatIds = new Set<string>();
    const seenUserIds = new Set<string>();
    const activeUnseatedUserIds = new Set(
      activeUnseatedUsers.map((user) => user.id),
    );
    const availableSeatIds = new Set(availableSeats.map((seat) => seat.id));

    for (const assignment of assignments) {
      if (seenSeatIds.has(assignment.seatId)) {
        setError("A seat can only be assigned once.");
        return;
      }
      if (seenUserIds.has(assignment.userId)) {
        setError("A user can only receive one seat.");
        return;
      }
      if (!availableSeatIds.has(assignment.seatId)) {
        setError("One of the selected seats is no longer available.");
        return;
      }
      if (!activeUnseatedUserIds.has(assignment.userId)) {
        setError(
          "One of the selected users already has a seat or is inactive.",
        );
        return;
      }
      seenSeatIds.add(assignment.seatId);
      seenUserIds.add(assignment.userId);
    }

    const previousSeats = seats;
    const now = new Date().toISOString();
    const userById = new Map(users.map((user) => [user.id, user]));
    const assignmentBySeatId = new Map(
      assignments.map((assignment) => [assignment.seatId, assignment.userId]),
    );
    let latestSeats = seats.map((seat) => {
      const userId = assignmentBySeatId.get(seat.id);
      if (!userId) return seat;
      return {
        ...seat,
        assignedUserId: userId,
        assignedUser: userById.get(userId),
        lastUse: now,
        lastReassign: now,
      };
    });

    setSavingSeatId("auto");
    setError(null);
    try {
      applySeats(latestSeats);
      for (const assignment of assignments) {
        const response = await apiClient.assignCompanySeat(
          companyId,
          assignment.seatId,
          assignment.userId,
        );
        latestSeats = latestSeats.map((seat) =>
          seat.id === assignment.seatId ? response.seat : seat,
        );
        applySeats(latestSeats);
      }
      setAutoAssignDialogOpen(false);
      setAutoAssignSelections({});
    } catch (err) {
      applySeats(previousSeats);
      setError(
        err instanceof Error ? err.message : "Failed to auto-assign seats",
      );
    } finally {
      setSavingSeatId(null);
    }
  };

  const handleAutoAssignSeats = async () => {
    if (activeUnseatedUsers.length === 0) {
      setError("All active users already have seats.");
      return;
    }
    if (availableSeats.length === 0) {
      setError("There are no available seats to assign.");
      return;
    }

    if (availableSeats.length >= activeUnseatedUsers.length) {
      await applySeatAssignments(
        activeUnseatedUsers.map((user, index) => ({
          userId: user.id,
          seatId: availableSeats[index].id,
        })),
      );
      return;
    }

    setAutoAssignSelections(
      Object.fromEntries(
        availableSeats.map((seat, index) => [
          seat.id,
          activeUnseatedUsers[index]?.id ?? "",
        ]),
      ),
    );
    setAutoAssignDialogOpen(true);
  };

  const handleUpdateSeatSettings = async (
    seat: SeatWithUser,
    settings: { isFlexible?: boolean; autoReassign?: boolean },
  ) => {
    const previousSeats = seats;
    const optimisticSeats = seats.map((candidate) => {
      if (candidate.id !== seat.id) return candidate;
      const nextIsFlexible = settings.isFlexible ?? !!candidate.isFlexible;
      return {
        ...candidate,
        ...settings,
        autoReassign: nextIsFlexible
          ? (settings.autoReassign ?? candidate.autoReassign)
          : false,
      };
    });

    setSavingSeatId(seat.id);
    setError(null);
    try {
      applySeats(optimisticSeats);
      const response = await apiClient.updateCompanySeat(
        companyId,
        seat.id,
        settings,
      );
      applySeats(
        optimisticSeats.map((candidate) =>
          candidate.id === seat.id ? response.seat : candidate,
        ),
      );
    } catch (err) {
      applySeats(previousSeats);
      setError(err instanceof Error ? err.message : "Failed to update seat");
    } finally {
      setSavingSeatId(null);
    }
  };

  const handleDeleteSeat = async (seat: SeatWithUser) => {
    if (!window.confirm(`Delete seat ${seat.id}?`)) return;
    const previousSeats = seats;
    setSavingSeatId(seat.id);
    setError(null);
    try {
      applySeats(seats.filter((candidate) => candidate.id !== seat.id));
      await apiClient.deleteCompanySeat(companyId, seat.id);
    } catch (err) {
      applySeats(previousSeats);
      setError(err instanceof Error ? err.message : "Failed to delete seat");
    } finally {
      setSavingSeatId(null);
    }
  };

  const openAssignDialog = (seat: SeatWithUser) => {
    setAssigningSeat(seat);
    setSelectedUserId(seat.assignedUserId ?? "");
  };

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<AutoAwesomeIcon />}
            onClick={handleAutoAssignSeats}
            disabled={
              loading ||
              savingSeatId !== null ||
              activeUnseatedUsers.length === 0 ||
              availableSeats.length === 0
            }
          >
            Autoassign Seats
          </Button>
          {!hideHeaderActions && (
            <>
              <Button
                size="small"
                startIcon={<RefreshIcon />}
                onClick={loadSeats}
                disabled={loading}
              >
                Refresh
              </Button>
              <Button
                size="small"
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setAddDialogOpen(true)}
                disabled={savingSeatId === "new"}
              >
                Add Seats
              </Button>
            </>
          )}
        </Box>
      </Box>

      <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
        Users can exist without a seat, but they need a seat to use Bayescase.
        Named seats stay assigned to one person; flexible seats can be
        reassigned automatically after 30 days of inactivity.
      </Alert>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
          <CircularProgress size={28} />
        </Box>
      ) : seats.length > 0 ? (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Assigned User</TableCell>
                <TableCell>Last Use</TableCell>
                <TableCell>Last Reassigned</TableCell>
                <TableCell>Seat Type</TableCell>
                <TableCell>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    Auto Reassign
                    <Tooltip title="Seats are automatically reassigned to the next user that logs in after 30 days of inactivity of the assigned user.">
                      <HelpOutlineIcon fontSize="small" color="action" />
                    </Tooltip>
                  </Box>
                </TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {seats.map((seat) => {
                const isFlexible = isFlexibleSeat(seat);

                return (
                  <TableRow key={seat.id}>
                    <TableCell>
                      <Tooltip title={seat.id}>
                        <Typography
                          variant="caption"
                          sx={{ fontFamily: "monospace" }}
                        >
                          {seat.id.slice(0, 8)}...
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      {seat.assignedUser ? (
                        <Box>
                          <Typography variant="body2">
                            {formatUserName(seat.assignedUser)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {seat.assignedUser.email}
                          </Typography>
                        </Box>
                      ) : seat.assignedUserId ? (
                        <Typography variant="caption" color="text.secondary">
                          {seat.assignedUserId}
                        </Typography>
                      ) : (
                        <Chip
                          label="Unassigned"
                          size="small"
                          variant="outlined"
                        />
                      )}
                    </TableCell>
                    <TableCell>{formatDate(seat.lastUse)}</TableCell>
                    <TableCell>{formatDate(seat.lastReassign)}</TableCell>
                    <TableCell>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Typography variant="caption">Named</Typography>
                        <Switch
                          size="small"
                          checked={isFlexible}
                          disabled={savingSeatId === seat.id}
                          sx={{
                            "& .MuiSwitch-switchBase": {
                              color: "primary.main",
                            },
                            "& .MuiSwitch-switchBase.Mui-checked": {
                              color: "primary.main",
                            },
                            "& .MuiSwitch-track": {
                              backgroundColor: "primary.light",
                              opacity: 0.55,
                            },
                            "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                              {
                                backgroundColor: "primary.light",
                                opacity: 0.55,
                              },
                          }}
                          onChange={(_, checked) =>
                            handleUpdateSeatSettings(seat, {
                              isFlexible: checked,
                            })
                          }
                        />
                        <Typography variant="caption">Flexible</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <FormControlLabel
                        sx={{ m: 0 }}
                        control={
                          <Switch
                            size="small"
                            checked={isFlexible && seat.autoReassign}
                            disabled={!isFlexible || savingSeatId === seat.id}
                            onChange={(_, checked) =>
                              handleUpdateSeatSettings(seat, {
                                autoReassign: checked,
                              })
                            }
                          />
                        }
                        label={isFlexible && seat.autoReassign ? "On" : "Off"}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "flex-end",
                          gap: 1,
                        }}
                      >
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => openAssignDialog(seat)}
                          disabled={savingSeatId === seat.id}
                        >
                          {seat.assignedUserId ? "Reassign" : "Assign"}
                        </Button>
                        {seat.assignedUserId && (
                          <Button
                            size="small"
                            variant="outlined"
                            color="warning"
                            onClick={() => handleAssignSeat(seat.id, null)}
                            disabled={savingSeatId === seat.id}
                          >
                            Unassign
                          </Button>
                        )}
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteSeat(seat)}
                          disabled={savingSeatId === seat.id}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Typography
          variant="body2"
          color="text.secondary"
          align="center"
          sx={{ py: 2 }}
        >
          No seats found
        </Typography>
      )}

      <Dialog
        open={isAddDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Add Seats</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            margin="dense"
            label="Number of seats"
            type="number"
            value={newSeatCount}
            onChange={(event) => setNewSeatCount(event.target.value)}
            inputProps={{ min: 1, max: 100 }}
            sx={{ mb: 2 }}
          />
          <FormControl fullWidth>
            <InputLabel>Seat type</InputLabel>
            <Select
              value={newSeatsFlexible ? "flexible" : "named"}
              label="Seat type"
              onChange={(event) =>
                setNewSeatsFlexible(event.target.value === "flexible")
              }
            >
              <MenuItem value="flexible">Flexible</MenuItem>
              <MenuItem value="named">Named</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleAddSeats}
            disabled={savingSeatId === "new"}
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={autoAssignDialogOpen}
        onClose={() => setAutoAssignDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Autoassign Seats</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            There are fewer available seats than active users without seats.
            Select which users should receive the available seats.
          </Typography>

          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Seat</TableCell>
                  <TableCell>User</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {availableSeats.map((seat) => {
                  const currentUserId = autoAssignSelections[seat.id] ?? "";
                  const selectableUsers = activeUnseatedUsers.filter(
                    (user) =>
                      user.id === currentUserId ||
                      !selectedAutoAssignUserIds.has(user.id),
                  );

                  return (
                    <TableRow key={seat.id}>
                      <TableCell>{formatSeatLabel(seat)}</TableCell>
                      <TableCell>
                        <FormControl fullWidth size="small">
                          <InputLabel>User</InputLabel>
                          <Select
                            value={currentUserId}
                            label="User"
                            onChange={(event) =>
                              setAutoAssignSelections((current) => ({
                                ...current,
                                [seat.id]: event.target.value,
                              }))
                            }
                          >
                            <MenuItem value="">No user</MenuItem>
                            {selectableUsers.map((user) => (
                              <MenuItem key={user.id} value={user.id}>
                                {formatUserName(user)} ({user.email})
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAutoAssignDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={savingSeatId === "auto"}
            onClick={() =>
              applySeatAssignments(
                Object.entries(autoAssignSelections)
                  .filter(([, userId]) => Boolean(userId))
                  .map(([seatId, userId]) => ({ seatId, userId })),
              )
            }
          >
            Assign Seats
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!assigningSeat}
        onClose={() => setAssigningSeat(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Assign Seat</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 1 }}>
            <InputLabel>User</InputLabel>
            <Select
              value={selectedUserId}
              label="User"
              onChange={(event) => setSelectedUserId(event.target.value)}
            >
              <MenuItem value="">Unassigned</MenuItem>
              {assignableUsers.map((user) => (
                <MenuItem key={user.id} value={user.id}>
                  {formatUserName(user)} ({user.email})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssigningSeat(null)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => {
              if (assigningSeat) {
                handleAssignSeat(assigningSeat.id, selectedUserId || null);
              }
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
